import React, {useCallback, useEffect, useMemo} from "react";
import {Table} from "antd";
import {useTranslate} from "../../../../hooks/translate.hook";
import {createSystemAccessColumns} from "./system.accesses.columns";
import {
    createAccessesBySystemId,
    createGroupsBySystemId,
    getDefaultGroup,
    resolveDefaultGroups,
} from "./system.accesses.helpers";
import {SystemAccessesTableProps, SystemAccessFormValue} from "./system.accesses.types";

const EMPTY_SYSTEM_ACCESSES: SystemAccessFormValue[] = [];

export const SystemAccessesTable: React.FC<SystemAccessesTableProps> = ({
    systems,
    systemGroups,
    loading,
    systemsLoading,
    groupsLoading,
    value,
    onChange,
}) => {
    const t = useTranslate();
    const accesses = value ?? EMPTY_SYSTEM_ACCESSES;
    const groupsBySystemId = useMemo(() => createGroupsBySystemId(systemGroups), [systemGroups]);
    const accessesBySystemId = useMemo(() => createAccessesBySystemId(accesses), [accesses]);

    useEffect(() => {
        if (!onChange || groupsLoading) {
            return;
        }

        const accessesWithDefaults = resolveDefaultGroups(accesses, groupsBySystemId);
        if (accessesWithDefaults !== accesses) {
            onChange(accessesWithDefaults);
        }
    }, [accesses, groupsBySystemId, groupsLoading, onChange]);

    const toggleSystem = useCallback((systemId: string, checked: boolean) => {
        if (!onChange) {
            return;
        }

        if (!checked) {
            onChange(accesses.filter(({system}) => system !== systemId));
            return;
        }

        if (accessesBySystemId.has(systemId)) {
            return;
        }

        const access: SystemAccessFormValue = {system: systemId};
        const defaultGroup = getDefaultGroup(systemId, groupsBySystemId);
        if (defaultGroup) {
            access.group = defaultGroup.id;
        }

        onChange([...accesses, access]);
    }, [accesses, accessesBySystemId, groupsBySystemId, onChange]);

    const selectGroup = useCallback((systemId: string, groupId: number) => {
        if (!onChange) {
            return;
        }

        onChange(accesses.map((access) =>
            access.system === systemId ? {...access, group: groupId} : access
        ));
    }, [accesses, onChange]);

    const columns = useMemo(() => createSystemAccessColumns({
        t,
        loading,
        groupsLoading,
        accessesBySystemId,
        groupsBySystemId,
        onToggleSystem: toggleSystem,
        onSelectGroup: selectGroup,
    }), [
        accessesBySystemId,
        groupsBySystemId,
        groupsLoading,
        loading,
        selectGroup,
        t,
        toggleSystem,
    ]);

    return (
        <Table
            bordered
            size="small"
            rowKey="id"
            columns={columns}
            dataSource={systems}
            loading={systemsLoading}
            pagination={false}
        />
    );
};
